import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ANNOTATION_SOURCE_LOCATION,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { Breadcrumbs, Link } from '@backstage/core-components';
import { alertApiRef, configApiRef, useApi } from '@backstage/core-plugin-api';
import { Helmet } from 'react-helmet';
import {
  EntityRefLink,
  FavoriteEntity,
  InspectEntityDialog,
  UnregisterEntityDialog,
  catalogApiRef,
} from '@backstage/plugin-catalog-react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  makeStyles,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { EDIT_URL } from './CatalogDefectsBanner';
import {
  catalogEntityDeletePermission,
  catalogEntityRefreshPermission,
} from '@backstage/plugin-catalog-common/alpha';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import { FactsBar } from './FactsBar';
import { useComponentPage } from './ComponentPageContext';
import { targetsOf } from './relations';
import { observabilityOf } from './observability';

const useStyles = makeStyles(theme => ({
  header: {
    // Backstage's Page is a grid of named areas; the stock header sits in
    // `pageHeader`, and so must this one, or the grid places it in the next
    // free cell and squeezes the tab content beside it.
    gridArea: 'pageHeader',
    minWidth: 0,
    padding: theme.spacing(3, 3, 2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  top: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    // On a phone the actions go below the title whatever its length; wrapping
    // alone keeps a short title's actions beside it, squeezed.
    [theme.breakpoints.down('xs')]: { flexDirection: 'column' },
  },
  typeLine: {
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
    letterSpacing: '0.02em',
  },
  titleRow: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  title: {
    fontSize: 'var(--text-h3)',
    lineHeight: 'var(--text-h3-line-height)',
    fontWeight: 700,
    margin: 0,
    overflowWrap: 'anywhere',
  },
  description: { maxWidth: '75ch', color: 'var(--ui-text-muted)' },
  meta: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1, 2),
    fontSize: '0.875rem',
  },
  metaLabel: { color: 'var(--ui-text-muted)', marginRight: theme.spacing(0.5) },
  tag: { fontFamily: 'var(--font-mono)', fontSize: '0.75rem' },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    // Three actions and the menu are wider than a phone (AC-14).
    flexWrap: 'wrap',
  },
}));

/** Where the source lives: the catalog's source location, else the repo. */
function sourceUrlOf(
  annotations: Record<string, string>,
  slug?: string,
): string | undefined {
  const location = annotations[ANNOTATION_SOURCE_LOCATION];
  if (location?.startsWith('url:')) return location.slice('url:'.length);
  return slug ? `https://github.com/${slug}` : undefined;
}

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * The top of a component's page (specs/component-page.md): where it sits, what
 * it is, who owns it, and the ways out of the portal a reader reaches for — its
 * source, the workflow it is deployed with, and its logs. The portal does not
 * deploy: *Deploy* opens that workflow in GitHub Actions, where it is dispatched.
 */
export const ComponentHeader = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const catalogApi = useApi(catalogApiRef);
  const appTitle =
    useApi(configApiRef).getOptionalString('app.title') ?? 'Backstage';
  const alertApi = useApi(alertApiRef);
  const { entity, slug, pipeline } = useComponentPage();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  // As the catalog's own menu does: an action the backend would refuse is
  // offered disabled rather than failing after the click.
  const canRefresh = useEntityPermission(
    catalogEntityRefreshPermission,
  ).allowed;
  const canUnregister = useEntityPermission(
    catalogEntityDeletePermission,
  ).allowed;
  const [inspecting, setInspecting] = useState(false);
  const [unregistering, setUnregistering] = useState(false);

  const annotations = entity.metadata.annotations ?? {};
  const spec = (entity.spec ?? {}) as Record<string, unknown>;
  const title = entity.metadata.title ?? entity.metadata.name;
  const type = typeof spec.type === 'string' ? spec.type : undefined;
  const lifecycle = typeof spec.lifecycle === 'string' ? spec.lifecycle : '';
  const system = targetsOf(entity, RELATION_PART_OF).find(
    ref => parseEntityRef(ref).kind.toLowerCase() === 'system',
  );
  const owner = targetsOf(entity, RELATION_OWNED_BY)[0];
  const sourceUrl = sourceUrlOf(annotations, slug);
  const editUrl = annotations[EDIT_URL];

  // Deploy leads to the workflow the newest deployment ran, else to the first
  // deploy workflow still switched on. None: no button — a library has none.
  const workflows = pipeline.data?.deployWorkflows.filter(w => w.active) ?? [];
  const deployedWith = pipeline.data?.latestDeployment?.workflowPath;
  const deploy =
    workflows.find(w => w.path === deployedWith) ?? workflows[0] ?? undefined;
  // Logs only for a component whose annotation names its stack (DX-358).
  const { logs } = observabilityOf(entity);

  const refresh = async () => {
    setMenuAnchor(null);
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
      alertApi.post({
        message: 'Refresh scheduled — the catalog reads the entity again.',
        severity: 'info',
        display: 'transient',
      });
    } catch (err) {
      alertApi.post({
        message: `Could not schedule a refresh: ${(err as Error).message}`,
        severity: 'error',
      });
    }
  };

  return (
    <header className={classes.header}>
      {/* The tab sets the middle part: "AI Gateway | Overview | <portal>", as
          the catalog's own entity header titles a page. */}
      <Helmet
        titleTemplate={`${title} | %s | ${appTitle}`}
        defaultTitle={`${title} | ${appTitle}`}
      />
      <Breadcrumbs aria-label="Breadcrumb">
        <Link to="/catalog">Catalog</Link>
        {system && <EntityRefLink entityRef={system} disableTooltip />}
        <Typography component="span" style={{ fontStyle: 'normal' }}>
          {title}
        </Typography>
      </Breadcrumbs>

      <div className={classes.top}>
        <Box minWidth={0}>
          <div className={classes.typeLine}>
            Component{type ? ` · ${capitalise(type)}` : ''}
          </div>
          <div className={classes.titleRow}>
            <h1 className={classes.title}>{title}</h1>
            <FavoriteEntity entity={entity} />
          </div>
        </Box>
        <div className={classes.actions}>
          {sourceUrl && (
            <Button
              variant="outlined"
              href={sourceUrl}
              target="_blank"
              rel="noopener"
            >
              View source
            </Button>
          )}
          {/* Outlined like View source, not the page's filled main action
              (DX-391): it is a way to the place that deploys, and a filled
              brand-coloured button read as "click here and it deploys". */}
          {deploy && (
            <Button
              variant="outlined"
              href={deploy.actionsUrl}
              target="_blank"
              rel="noopener"
              title={`Opens ${deploy.name} in GitHub Actions`}
            >
              Deploy
            </Button>
          )}
          {logs && (
            <Button
              variant="outlined"
              href={logs.url}
              target="_blank"
              rel="noopener"
              title={`Opens the logs of stack ${logs.stack} in Grafana`}
            >
              Logs
            </Button>
          )}
          <IconButton
            aria-label="More actions"
            aria-haspopup="menu"
            onClick={e => setMenuAnchor(e.currentTarget)}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem onClick={refresh} disabled={!canRefresh}>
              Refresh from the catalog file
            </MenuItem>
            {editUrl && (
              <MenuItem
                component="a"
                href={editUrl}
                target="_blank"
                rel="noopener"
                onClick={() => setMenuAnchor(null)}
              >
                Edit catalog-info.yaml
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                setInspecting(true);
              }}
            >
              Inspect entity
            </MenuItem>
            <MenuItem
              disabled={!canUnregister}
              onClick={() => {
                setMenuAnchor(null);
                setUnregistering(true);
              }}
            >
              Unregister entity
            </MenuItem>
          </Menu>
        </div>
      </div>

      {entity.metadata.description && (
        <Typography className={classes.description}>
          {entity.metadata.description}
        </Typography>
      )}

      <div className={classes.meta} role="group" aria-label="About">
        {lifecycle && <Chip size="small" label={lifecycle} />}
        {owner && (
          <span>
            <span className={classes.metaLabel}>Owner</span>
            <EntityRefLink entityRef={owner} disableTooltip />
          </span>
        )}
        {system && (
          <span>
            <span className={classes.metaLabel}>System</span>
            <EntityRefLink entityRef={system} disableTooltip />
          </span>
        )}
        {(entity.metadata.tags ?? []).map(tag => (
          <Chip
            key={tag}
            size="small"
            variant="outlined"
            label={tag}
            className={classes.tag}
          />
        ))}
      </div>

      <FactsBar />

      <InspectEntityDialog
        entity={entity}
        open={inspecting}
        onClose={() => setInspecting(false)}
      />
      <UnregisterEntityDialog
        entity={entity}
        open={unregistering}
        onClose={() => setUnregistering(false)}
        onConfirm={() => {
          setUnregistering(false);
          navigate('/catalog');
        }}
      />
    </header>
  );
};
